/* STENCIL IMPORTS */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter
} from "@stencil/core";
/* OTHER LIBRARIES IMPORTS */
/* CUSTOM IMPORTS */
import { config } from "../../../common/config";
import { Locale } from "../../../common/locale";

@Component({
  tag: "gx-ide-bpm-timer-duration",
  styleUrl: "bpm-timer-duration.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/bpm-timer-duration"]
})
export class GxIdeBpmTimerDuration {
  // 1.OWN PROPERTIES //

  /**
   * The component hard-coded strings translations.
   */
  private _componentLocale: any;
  private renderedFirstTime = false;

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeBpmTimerDurationElement;

  private yearsEl!: HTMLInputElement;
  private monthsEl!: HTMLInputElement;
  private daysEl!: HTMLInputElement;
  private hoursEl!: HTMLInputElement;
  private minutesEl!: HTMLInputElement;
  private secondsEl!: HTMLInputElement;

  // 3.STATE() VARIABLES //

  // 4.PUBLIC PROPERTY API | WATCH'S //

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;

  /**
   * Current value for timer.
   */
  @Prop() readonly duration?: TimerDuration;

  /**
   *  Callback invoked when user confirms application declaration, (OK Button)
   */
  @Prop() readonly confirmCallback: (data: TimerDuration) => Promise<void>;

  /**
   * Callback invoked when user cancels application declaration */
  @Prop() readonly cancelCallback: () => Promise<void>;

  // 5.EVENTS (EMIT) //

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  // 6.COMPONENT LIFECYCLE METHODS //

  async componentWillLoad() {
    this._componentLocale = await Locale.getComponentStrings(this.el);
  }

  componentDidRender() {
    if (!this.renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this._componentLocale.componentName
      );
      this.renderedFirstTime = true;
    }
  }

  // 7.LISTENERS //

  // 8.PUBLIC METHODS API //

  // 9.LOCAL METHODS //

  private confirmCallbackHandler = async () => {
    if (this.confirmCallback) {
      const data: TimerDuration = {
        years: parseInt(this.yearsEl.value),
        months: parseInt(this.monthsEl.value),
        days: parseInt(this.daysEl.value),
        hours: parseInt(this.hoursEl.value),
        minutes: parseInt(this.minutesEl.value),
        seconds: parseInt(this.secondsEl.value)
      };

      await this.confirmCallback(data);
    }
  };

  // 10.RENDER() FUNCTION //

  render() {
    return (
      <Host class="gx-ide-component">
        <div class="gx-ide-main-wrapper">
          <gx-ide-container slimmerFooter={config.gxIdeContainer.slimmerFooter}>
            <div class="control-wrapper control-wrapper--vertical">
              <label htmlFor="years">
                {this._componentLocale.controls.years}
              </label>
              <input
                type="number"
                id="years"
                class="input-text input-text--full-width"
                value={this.duration?.years.toString()}
                ref={el => (this.yearsEl = el as HTMLInputElement)}
              />
            </div>

            <div class="control-wrapper control-wrapper--vertical">
              <label htmlFor="months">
                {this._componentLocale.controls.months}
              </label>
              <input
                type="number"
                id="months"
                class="input-text input-text--full-width"
                value={this.duration?.months.toString()}
                ref={el => (this.monthsEl = el as HTMLInputElement)}
              />
            </div>

            <div class="control-wrapper control-wrapper--vertical">
              <label htmlFor="days">
                {this._componentLocale.controls.days}
              </label>
              <input
                type="number"
                id="days"
                class="input-text input-text--full-width"
                value={this.duration?.days.toString()}
                ref={el => (this.daysEl = el as HTMLInputElement)}
              />
            </div>

            <div class="control-wrapper control-wrapper--vertical">
              <label htmlFor="hours">
                {this._componentLocale.controls.hours}
              </label>
              <input
                type="number"
                id="hours"
                class="input-text input-text--full-width"
                value={this.duration?.hours.toString()}
                ref={el => (this.hoursEl = el as HTMLInputElement)}
              />
            </div>

            <div class="control-wrapper control-wrapper--vertical">
              <label htmlFor="minutes">
                {this._componentLocale.controls.minutes}
              </label>
              <input
                type="number"
                id="minutes"
                class="input-text input-text--full-width"
                value={this.duration?.minutes.toString()}
                ref={el => (this.minutesEl = el as HTMLInputElement)}
              />
            </div>

            <div class="control-wrapper control-wrapper--vertical">
              <label htmlFor="seconds">
                {this._componentLocale.controls.seconds}
              </label>
              <input
                type="number"
                id="seconds"
                class="input-text input-text--full-width"
                value={this.duration?.seconds.toString()}
                ref={el => (this.secondsEl = el as HTMLInputElement)}
              />
            </div>

            <div class="controls-wrapper">
              <button onClick={this.confirmCallbackHandler}>
                {this._componentLocale.controls.ok}
              </button>
              <button onClick={this.cancelCallback}>
                {this._componentLocale.controls.cancel}
              </button>
            </div>
          </gx-ide-container>
        </div>
      </Host>
    );
  }
}

export type TimerDuration = {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};
